// BUILD-046 correction cycle 1bb88b3e (stage B1, items N5 and N6): the flow that runs "Complete task" from the Done-with-a-note panel against the typed
// client. doneNoteModel.ts holds the rules (what is saved, what is kept, what is said); this module sequences them, as doneFlow.ts does for ordinary Done.
//
//  - The states change BEFORE the request is sent (the row's Done is disabled at once; a second click sends nothing).
//  - Exactly one request: POST /work/{id}/complete, with { note } only when text was typed, otherwise with no body (an ordinary Done).
//  - On an accepted answer the note is "saved" only when the answer says `noteSaved === true` (doneNoteModel.classifyAnswer); the caller merges the
//    SERVER's item into the list and fires `shared-tasks:changed` whenever the task is completed (also when the note was not saved).
//  - On any failure the task is still open, the typed text and date stay, and the person can try again; the message says the task was not completed
//    (or that the result could not be confirmed).
//  - An answer dropped because the Caller changed is neither a success nor a failure (the Caller change resets both states).
//
// Pure apart from the injected api. Runtime imports: the note model. Erasable TypeScript only.

import { attemptNoteComplete, classifyAnswer, noteCompleted, noteFailed } from './doneNoteModel';
import type { Classified, CompleteAnswer, DoneNoteState, WorkNote } from './doneNoteModel';
import type { DoneStore } from './doneFlow';

export interface DoneNoteApi {
  complete: (id: string, note?: WorkNote) => Promise<CompleteAnswer & { item: unknown }>;
}

export interface NoteStore {
  get: () => DoneNoteState;
  set: (next: DoneNoteState) => void;
}

export type DoneNoteOutcome =
  | { kind: 'ignored' }
  | { kind: 'done'; item: unknown; result: Classified['kind']; completedNow: boolean }
  | { kind: 'failed' }
  | { kind: 'dropped' };

function isStale(thrown: unknown): boolean {
  return thrown !== null && typeof thrown === 'object' && (thrown as { kind?: unknown }).kind === 'stale';
}

/** "Complete task" for one row (`status` is the status the row shows now, `today` the Phoenix date). */
export async function performDoneWithNote(api: DoneNoteApi, done: DoneStore, note: NoteStore, id: string, status: string, today: string): Promise<DoneNoteOutcome> {
  const step = attemptNoteComplete(note.get(), done.get(), id, status, today);
  note.set(step.note);
  done.set(step.done);
  if (step.send === null) return { kind: 'ignored' };
  const noteSent = step.send.note !== null;
  try {
    const answer = step.send.note === null ? await api.complete(id) : await api.complete(id, step.send.note);
    const applied = noteCompleted(note.get(), done.get(), id, answer, noteSent);
    note.set(applied.note);
    done.set(applied.done);
    const classified = classifyAnswer(answer, noteSent);
    return { kind: 'done', item: answer.item, result: classified.kind, completedNow: classified.completedNow };
  } catch (thrown) {
    if (isStale(thrown)) return { kind: 'dropped' };
    const applied = noteFailed(note.get(), done.get(), id, thrown);
    note.set(applied.note);
    done.set(applied.done);
    return { kind: 'failed' };
  }
}
